import { useFormikContext } from 'formik';
import React from 'react';
import { Button, Grid, Header5Text, ModalWindow, Spinner, SwitchDialogWindows, TextField } from 'ui-toolkit';
import { SortableList } from 'components/sorting/SortableList';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IMergeDocumentValues } from './MergeDocumentForm';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { DocumentCategorySelector } from '../categorySelect/DocumentCategorySelector';

interface IMergeDocumentModalProps {
  fetchStatus: IFetchStatus;
  fetchError: string;
  onClose: () => void;
}

export const MergeDocumentModal: React.FC<IMergeDocumentModalProps> = ({ fetchStatus, fetchError, onClose }) => {
  const formik = useFormikContext<IMergeDocumentValues>();
  const isValid = formik.isValid;
  const getDocumentItemContent = (document: IStandardDocumentVersionData) => (
    <Grid templateColumns={'3fr 2fr'}>
      <span>{document.fileName}</span>
      <span>{document.category}</span>
    </Grid>
  );

  const onDelete = (document: IStandardDocumentVersionData) => {
    formik.setFieldTouched('selectedDocuments');
    formik.setFieldValue(
      'selectedDocuments',
      formik.values.selectedDocuments.filter((d) => d.id !== document.id)
    );
  };

  const onSort = (documents: IStandardDocumentVersionData[]) => {
    formik.setFieldTouched('selectedDocuments');
    formik.setFieldValue('selectedDocuments', documents);
  };

  const onCloseHelper = () => {
    formik.resetForm();
    onClose();
  };

  const selectedCategory = formik.values.category?.name ? formik.values.category?.label : 'document';

  const hasMultipleDocuments = formik.values.selectedDocuments.length > 1;

  return (
    <SwitchDialogWindows
      errorTitle={fetchError}
      isLoading={fetchStatus.fetching}
      isCompleted={fetchStatus.fetched}
      isError={fetchStatus.fetched && !fetchStatus.success}
      loadingTitle={'Combining Documents'}
      onCloseClick={onCloseHelper}
      successTitle={`Documents combined successfully. Your ${selectedCategory} was created.`}
    >
      <ModalWindow onClose={onCloseHelper} title="Combine Documents">
        <Grid>
          <Grid templateColumns={'3fr 2fr'}>
            <Header5Text>File</Header5Text>
            <Header5Text>Category</Header5Text>
          </Grid>
          <SortableList
            content={getDocumentItemContent}
            items={formik.values.selectedDocuments}
            onDelete={onDelete}
            onSort={onSort}
            showDelete={hasMultipleDocuments}
          />
          <Grid templateColumns={'2fr 3fr'}>
            <DocumentCategorySelector
              data-test-id="merge-document-modal-categories"
              name="category"
              menuPosition="fixed"
              label="Category"
              isRequired={true}
              placeholder="Select A Category"
              selectedCategory={formik.values.category}
              onChange={(value: any) => {
                formik.setFieldTouched('category');
                formik.setFieldValue('category', value ? value.value : undefined);
              }}
            />
            {formik.values.category?.isRepeatable && (
              <TextField name="fileName" label="Document Name" isRequired={true} />
            )}
          </Grid>
          {formik.isSubmitting && <Spinner />}
          {!formik.isSubmitting && (
            <Button intent="secondary" type="submit" disabled={!isValid} data-test-id={'package-button'}>
              Package
            </Button>
          )}
        </Grid>
      </ModalWindow>
    </SwitchDialogWindows>
  );
};
